/**
 * Error Tracking Service
 * Captures and logs errors with context for debugging and monitoring
 * Can integrate with external services like Sentry, LogRocket, etc.
 */

class ErrorTrackingService {
  constructor() {
    this.isInitialized = false;
    this.context = {};
    this.errorQueue = [];
    this.maxQueueSize = 50;
  }

  /**
   * Initialize the error tracking service
   * @param {Object} config - Configuration options
   */
  initialize(config = {}) {
    if (this.isInitialized) {
      console.warn('Error tracking already initialized');
      return;
    }

    this.config = {
      enabled: config.enabled !== false,
      environment: config.environment || process.env.NODE_ENV || 'development',
      enableConsole: config.enableConsole !== false,
      sendToServer: config.sendToServer || false,
      serverEndpoint: config.serverEndpoint || '/api/v1/errors',
      // Sentry integration (optional)
      sentryDsn: config.sentryDsn || process.env.REACT_APP_SENTRY_DSN,
      ...config,
    };

    // Initialize Sentry if DSN provided
    if (this.config.sentryDsn && window.Sentry) {
      window.Sentry.init({
        dsn: this.config.sentryDsn,
        environment: this.config.environment,
        beforeSend: (event) => this.beforeSend(event),
      });
    }

    // Set up global error handlers
    this.setupGlobalHandlers();
    
    this.isInitialized = true;
    console.log(`✅ Error tracking initialized (${this.config.environment})`);
  }

  /**
   * Set up global error handlers
   */
  setupGlobalHandlers() {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.captureError(event.error || new Error(event.message), {
        type: 'unhandled_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        event.reason instanceof Error 
          ? event.reason 
          : new Error(String(event.reason)),
        {
          type: 'unhandled_rejection',
        }
      );
    });
  }

  /**
   * Set user context for error tracking
   * @param {Object} user - User information
   */
  setUser(user) {
    this.context.user = {
      id: user?.user_id,
      email: user?.email,
      role: user?.role,
    };

    if (window.Sentry) {
      window.Sentry.setUser(this.context.user);
    }
  }

  /**
   * Clear user context (on logout)
   */
  clearUser() {
    this.context.user = null;
    if (window.Sentry) {
      window.Sentry.setUser(null);
    }
  }

  /**
   * Set additional context
   * @param {string} key - Context key
   * @param {*} value - Context value
   */
  setContext(key, value) {
    this.context[key] = value;
    if (window.Sentry) {
      window.Sentry.setContext(key, value);
    }
  }

  /**
   * Capture an error with context
   * @param {Error} error - Error object
   * @param {Object} additionalContext - Additional context data
   */
  captureError(error, additionalContext = {}) {
    if (!this.config.enabled) return;

    const errorData = this.buildErrorData(error, additionalContext);

    // Log to console in development
    if (this.config.enableConsole) {
      console.error('🔴 Error captured:', errorData);
    }

    // Send to Sentry if available
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: { additional: additionalContext },
      });
    }

    // Add to local queue
    this.addToQueue(errorData);

    // Send to server if configured
    if (this.config.sendToServer) {
      this.sendToServer(errorData);
    }

    return errorData;
  }

  /**
   * Capture an API error
   * @param {Error} error - Axios error object
   * @param {Object} requestConfig - Request configuration
   */
  captureAPIError(error, requestConfig = {}) {
    const context = {
      type: 'api_error',
      url: error.config?.url || requestConfig.url,
      method: error.config?.method || requestConfig.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestData: error.config?.data || requestConfig.data,
    };

    return this.captureError(error, context);
  }

  /**
   * Capture a React component error
   * @param {Error} error - Error object
   * @param {Object} errorInfo - React error info with componentStack
   */
  captureComponentError(error, errorInfo) {
    const context = {
      type: 'react_error',
      componentStack: errorInfo?.componentStack,
    };

    return this.captureError(error, context);
  }

  /**
   * Log a message (non-error)
   * @param {string} message - Message to log
   * @param {string} level - Log level (info, warning, error)
   * @param {Object} context - Additional context
   */
  log(message, level = 'info', context = {}) {
    if (!this.config.enabled) return;

    const logData = {
      message,
      level,
      timestamp: new Date().toISOString(),
      context: { ...this.context, ...context },
    };

    if (this.config.enableConsole) {
      const emoji = level === 'error' ? '🔴' : level === 'warning' ? '⚠️' : 'ℹ️';
      console.log(`${emoji} ${message}`, context);
    }

    if (window.Sentry) {
      window.Sentry.captureMessage(message, level);
    }

    this.addToQueue(logData);
  }

  /**
   * Build comprehensive error data
   * @param {Error} error - Error object
   * @param {Object} additionalContext - Additional context
   * @returns {Object} Error data object
   */
  buildErrorData(error, additionalContext = {}) {
    return {
      message: error.message || 'Unknown error',
      name: error.name || 'Error',
      stack: error.stack,
      timestamp: new Date().toISOString(),
      environment: this.config.environment,
      context: {
        ...this.context,
        ...additionalContext,
        browser: this.getBrowserInfo(),
        page: {
          url: window.location.href,
          pathname: window.location.pathname,
          referrer: document.referrer,
        },
      },
    };
  }

  /**
   * Get browser information
   * @returns {Object} Browser info
   */
  getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
    };
  }

  /**
   * Add error to local queue
   * @param {Object} errorData - Error data
   */
  addToQueue(errorData) {
    this.errorQueue.push(errorData);
    
    // Keep queue size manageable
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  /**
   * Send error to server
   * @param {Object} errorData - Error data
   */
  async sendToServer(errorData) {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await fetch(this.config.serverEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(errorData),
      });
    } catch (err) {
      // Silently fail - don't create error loop
      if (this.config.enableConsole) {
        console.warn('Failed to send error to server:', err);
      }
    }
  }

  /**
   * Sentry beforeSend hook for filtering/modifying events
   * @param {Object} event - Sentry event
   * @returns {Object|null} Modified event or null to drop
   */
  beforeSend(event) {
    // Filter out development errors in production
    if (this.config.environment === 'production') {
      // Filter localhost errors
      if (event.request?.url?.includes('localhost')) {
        return null;
      }
    }

    // Add custom context
    event.contexts = {
      ...event.contexts,
      custom: this.context,
    };

    return event;
  }

  /**
   * Get error queue (for debugging)
   * @returns {Array} Error queue
   */
  getErrorQueue() {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue() {
    this.errorQueue = [];
  }

  /**
   * Test error tracking (development only)
   */
  test() {
    if (this.config.environment !== 'development') {
      console.warn('Error tracking test only available in development');
      return;
    }

    console.log('🧪 Testing error tracking...');
    
    // Test regular error
    this.captureError(new Error('Test error'), { test: true });
    
    // Test log
    this.log('Test log message', 'info', { test: true });
    
    console.log('✅ Test errors sent. Check console and queue.');
    console.log('Error queue:', this.getErrorQueue());
  }
}

// Create singleton instance
const errorTracker = new ErrorTrackingService();

export default errorTracker;
